import { useState, useEffect } from 'react';
import { financeConfigAPI, expenseCategoriesAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Settings, IndianRupee, Percent, AlertTriangle, Lock, Unlock,
  Save, Shield, Tags, Plus, Trash2, Loader2,
} from 'lucide-react';
import { cn } from '../lib/utils';

const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export const FinanceConfigPage = () => {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    default_doctor_share_percent: 30,
    gst_percent: 18,
    profit_alert_threshold: 10,
    month_lock_enabled: false,
  });
  // v38.3 — Expense Category management
  const [categories, setCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(true);
  const [newCat, setNewCat] = useState({ name: '', description: '' });
  const [catSaving, setCatSaving] = useState(false);

  const isSuperAdmin = user?.is_super_admin;
  const currentYear = new Date().getFullYear();

  const load = async () => {
    try {
      const res = await financeConfigAPI.get();
      setConfig(res.data);
      setForm({
        default_doctor_share_percent: res.data.default_doctor_share_percent ?? 30,
        gst_percent: res.data.gst_percent ?? 18,
        profit_alert_threshold: res.data.profit_alert_threshold ?? 10,
        month_lock_enabled: res.data.month_lock_enabled ?? false,
      });
    } catch { toast.error('Failed to load config'); }
    setLoading(false);
  };

  useEffect(() => { load(); loadCategories(); }, []);

  const loadCategories = async () => {
    setCatLoading(true);
    try {
      const res = await expenseCategoriesAPI.list();
      setCategories(res.data || []);
    } catch { toast.error('Failed to load expense categories'); }
    setCatLoading(false);
  };

  const handleAddCategory = async () => {
    if (!newCat.name.trim()) { toast.error('Category name is required'); return; }
    setCatSaving(true);
    try {
      await expenseCategoriesAPI.create({ name: newCat.name.trim(), description: newCat.description.trim() });
      toast.success(`Category "${newCat.name}" added`);
      setNewCat({ name: '', description: '' });
      await loadCategories();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create'); }
    setCatSaving(false);
  };

  const handleDeleteCategory = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"? Existing expenses tagged with it will not be removed.`)) return;
    try {
      await expenseCategoriesAPI.delete(cat.id);
      toast.success('Category removed');
      await loadCategories();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await financeConfigAPI.update(form);
      toast.success('Finance configuration updated');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const toggleMonthLock = async (month, year, lock) => {
    try {
      await financeConfigAPI.toggleMonthLock({ month, year, lock });
      toast.success(lock ? `${months[month - 1]} ${year} locked` : `${months[month - 1]} ${year} unlocked`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to toggle lock'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-500">Loading config...</div>;

  const lockedMonths = config?.locked_months || [];

  return (
    <div data-testid="finance-config-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Finance Configuration</h1>
          <p className="text-sm text-slate-500 mt-1">Configure financial parameters for P&L calculations</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Main Config */}
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h3 className="text-sm font-semibold text-slate-700 mb-5 flex items-center gap-2"><Settings size={14} /> General Settings</h3>
          <div className="space-y-5">
            <div>
              <Label className="flex items-center gap-1.5"><Percent size={13} className="text-slate-400" /> Default Doctor Share %</Label>
              <p className="text-xs text-slate-400 mb-2">Default revenue share percentage for doctors</p>
              <Input
                type="number"
                value={form.default_doctor_share_percent}
                onChange={(e) => setForm({...form, default_doctor_share_percent: Number(e.target.value)})}
                min={0}
                max={100}
                step={0.5}
                data-testid="doctor-share-input"
              />
            </div>
            <div>
              <Label className="flex items-center gap-1.5"><IndianRupee size={13} className="text-slate-400" /> GST %</Label>
              <p className="text-xs text-slate-400 mb-2">Goods & Services Tax percentage applied to invoices</p>
              <Input
                type="number"
                value={form.gst_percent}
                onChange={(e) => setForm({...form, gst_percent: Number(e.target.value)})}
                min={0}
                max={50}
                step={0.5}
                data-testid="gst-percent-input"
              />
            </div>
            <div>
              <Label className="flex items-center gap-1.5"><AlertTriangle size={13} className="text-amber-500" /> Profit Alert Threshold %</Label>
              <p className="text-xs text-slate-400 mb-2">Alert when profit margin falls below this percentage</p>
              <Input
                type="number"
                value={form.profit_alert_threshold}
                onChange={(e) => setForm({...form, profit_alert_threshold: Number(e.target.value)})}
                min={0}
                max={100}
                step={1}
                data-testid="profit-threshold-input"
              />
            </div>
            <div className="pt-2 border-t">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="flex items-center gap-1.5"><Lock size={13} className="text-slate-400" /> Month Lock Feature</Label>
                  <p className="text-xs text-slate-400 mt-0.5">Enable locking of monthly financial data to prevent edits</p>
                </div>
                <button
                  onClick={() => setForm({...form, month_lock_enabled: !form.month_lock_enabled})}
                  className={cn(
                    "relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200",
                    form.month_lock_enabled ? "bg-emerald-500" : "bg-slate-300"
                  )}
                  data-testid="month-lock-toggle"
                >
                  <span className={cn(
                    "inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 shadow-sm",
                    form.month_lock_enabled ? "translate-x-6" : "translate-x-1"
                  )} />
                </button>
              </div>
            </div>

            <Button onClick={handleSave} disabled={saving} className="w-full bg-emerald-600 hover:bg-emerald-700 gap-2 mt-3" data-testid="save-config-btn">
              <Save size={16} /> {saving ? 'Saving...' : 'Save Configuration'}
            </Button>
          </div>
        </div>

        {/* Month Lock Panel */}
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><Shield size={14} /> Month Lock Status</h3>
          {!config?.month_lock_enabled ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <Lock size={32} className="mb-3 text-slate-300" />
              <p className="text-sm font-medium">Month Lock is Disabled</p>
              <p className="text-xs mt-1">Enable the toggle in General Settings to use this feature</p>
            </div>
          ) : (
            <div>
              <p className="text-xs text-slate-400 mb-4">Lock months to prevent any financial data changes. {isSuperAdmin ? '' : 'Only admins can toggle locks.'}</p>
              <div className="grid grid-cols-3 gap-3">
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
                  const key = `${currentYear}-${m.toString().padStart(2, '0')}`;
                  const isLocked = lockedMonths.includes(key);
                  const isPast = m < new Date().getMonth() + 1;
                  return (
                    <button
                      key={m}
                      onClick={() => toggleMonthLock(m, currentYear, !isLocked)}
                      className={cn(
                        "flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-all",
                        isLocked
                          ? "bg-red-50 border-red-200 text-red-700"
                          : isPast
                            ? "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                            : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
                      )}
                      data-testid={`month-lock-${m}`}
                    >
                      {isLocked ? <Lock size={13} /> : <Unlock size={13} />}
                      <span className="flex-1 text-left text-xs">{months[m - 1].substring(0, 3)}</span>
                      {isLocked && <span className="text-[9px] bg-red-200 text-red-700 px-1 rounded">LOCKED</span>}
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-slate-400 mt-3">Year: {currentYear}</p>
            </div>
          )}
        </div>
      </div>

      {/* Current Config Summary */}
      {/* Expense Category management — v38.3 — separate from General settings */}
      <div className="mt-6 bg-white rounded-xl border border-slate-200 p-5" data-testid="expense-category-card">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2"><Tags size={14} className="text-violet-600" /> Expense Category</h3>
          <span className="text-[10px] text-slate-400">{categories.length} categor{categories.length === 1 ? 'y' : 'ies'}</span>
        </div>
        <p className="text-xs text-slate-500 mb-4">Define the buckets your finance team uses to tag every expense — e.g. <strong>Salary</strong>, <strong>Hardware</strong>, <strong>Maintenance</strong>, <strong>Miscellaneous</strong>, <strong>Marketing</strong>. These show up as a dropdown wherever an expense is recorded and drive the &lsquo;Expense by Category&rsquo; donut on the Finance Dashboard.</p>

        {/* Add new */}
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-2 items-end mb-3">
          <div>
            <Label className="text-[10px] font-semibold text-slate-500 uppercase mb-1 block">Category Name</Label>
            <Input value={newCat.name} onChange={(e) => setNewCat(c => ({ ...c, name: e.target.value }))} placeholder="e.g., Hardware" data-testid="exp-cat-name" />
          </div>
          <div>
            <Label className="text-[10px] font-semibold text-slate-500 uppercase mb-1 block">Description (optional)</Label>
            <Input value={newCat.description} onChange={(e) => setNewCat(c => ({ ...c, description: e.target.value }))} placeholder="Short note (what kinds of bills go here)" data-testid="exp-cat-desc" />
          </div>
          <Button onClick={handleAddCategory} disabled={catSaving} className="bg-violet-600 hover:bg-violet-700" data-testid="exp-cat-add">
            {catSaving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Plus className="h-4 w-4 mr-1.5" />} Add
          </Button>
        </div>

        {/* List */}
        {catLoading ? (
          <p className="text-xs text-slate-400 py-4 text-center">Loading…</p>
        ) : categories.length === 0 ? (
          <p className="text-xs text-slate-400 py-4 text-center">No categories yet. Add a few to organise expense entries.</p>
        ) : (
          <div className="overflow-x-auto border border-slate-200 rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-left">Description</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {categories.map(cat => (
                  <tr key={cat.id} className="border-t border-slate-100" data-testid={`exp-cat-row-${cat.id}`}>
                    <td className="px-3 py-2 font-medium text-slate-800">{cat.name}</td>
                    <td className="px-3 py-2 text-slate-500">{cat.description || '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-rose-600 hover:bg-rose-50" onClick={() => handleDeleteCategory(cat)} data-testid={`exp-cat-delete-${cat.id}`}>
                        <Trash2 size={12} className="mr-1" /> Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-6 bg-slate-50 rounded-lg border border-slate-200 p-5">
        <h3 className="text-xs font-semibold text-slate-500 uppercase mb-3">Current Configuration Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-slate-400">Doctor Share</p>
            <p className="text-lg font-bold text-slate-700">{config?.default_doctor_share_percent ?? 30}%</p>
          </div>
          <div>
            <p className="text-xs text-slate-400">GST</p>
            <p className="text-lg font-bold text-slate-700">{config?.gst_percent ?? 18}%</p>
          </div>
          <div>
            <p className="text-xs text-slate-400">Profit Alert</p>
            <p className="text-lg font-bold text-amber-600">&lt; {config?.profit_alert_threshold ?? 10}%</p>
          </div>
          <div>
            <p className="text-xs text-slate-400">Month Lock</p>
            <p className={cn("text-lg font-bold", config?.month_lock_enabled ? "text-emerald-600" : "text-slate-400")}>{config?.month_lock_enabled ? 'Enabled' : 'Disabled'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinanceConfigPage;
